import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { type Query, QueryClient } from '@tanstack/react-query';
import { del, get, set } from 'idb-keyval';
import { ApiRequestError } from './api-client';

/** Bumped whenever cached shapes change, invalidating every persisted cache. */
export const CACHE_BUSTER = 'ultrasnip-cache-v2';
const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000;

/**
 * Query-key roots whose data may be persisted to IndexedDB (fast warm start).
 * Allow-list only: never the profile/PII, never anything secret, never high-frequency ticks.
 */
export const PERSISTED_QUERY_ROOTS: ReadonlySet<string> = new Set([
  'token-metadata',
  'tracked-wallets',
  'strategies',
  'positions-snapshot',
]);

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: PERSIST_MAX_AGE,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/** IndexedDB persister; storage errors (private mode, quota) degrade to "no persistence". */
export const queryPersister = createAsyncStoragePersister({
  key: 'ultrasnip-query-cache',
  storage: {
    getItem: async (key) => {
      try {
        return (await get<string>(key)) ?? null;
      } catch {
        return null;
      }
    },
    setItem: async (key, value) => {
      try {
        await set(key, value);
      } catch {
        /* persistence is best-effort */
      }
    },
    removeItem: async (key) => {
      try {
        await del(key);
      } catch {
        /* persistence is best-effort */
      }
    },
  },
  throttleTime: 2_000,
});

export const persistOptions = {
  persister: queryPersister,
  maxAge: PERSIST_MAX_AGE,
  buster: CACHE_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) =>
      query.state.status === 'success' && PERSISTED_QUERY_ROOTS.has(String(query.queryKey[0])),
  },
};

/**
 * After a warm start from IndexedDB, persisted entries are shown at once but always revalidated:
 * the snapshot may predate the last change (writes are throttled), so it is never treated as fresh.
 */
export function revalidatePersisted(client: QueryClient): Promise<void> {
  return client.invalidateQueries({
    predicate: (query) => PERSISTED_QUERY_ROOTS.has(String(query.queryKey[0])),
  });
}

/** Wipes in-memory and persisted caches (on sign-out, so the next user never sees stale data). */
export async function clearAllCaches(client: QueryClient): Promise<void> {
  client.clear();
  await queryPersister.removeClient();
}
